import { useCallback, useEffect, useRef, useState } from 'react';
import { getMonitoringCards, getMonitoringCharts, getMonitoringMetrics } from 'services/monitoringAnalytics';
import { MonitoringQuery, MonitoringSectionData } from '../components/Monitoring/types';

export type MonitoringSectionKey =
  | 'summary'
  | 'accessTimeline'
  | 'accessTypes'
  | 'sessionDuration'
  | 'sessionLocations'
  | 'mostUsedLayers'
  | 'layerDownloads'
  | 'filterUsage';

export type MonitoringSectionState = {
  filters: MonitoringQuery;
  data: MonitoringSectionData | null;
  loading: boolean;
  error: string;
};

const sectionKeys: MonitoringSectionKey[] = [
  'summary',
  'accessTimeline',
  'accessTypes',
  'sessionDuration',
  'sessionLocations',
  'mostUsedLayers',
  'layerDownloads',
  'filterUsage',
];

const requirements: Record<MonitoringSectionKey, 'cards' | 'charts' | 'all'> = {
  summary: 'cards',
  accessTimeline: 'charts',
  accessTypes: 'all',
  sessionDuration: 'all',
  sessionLocations: 'charts',
  mostUsedLayers: 'charts',
  layerDownloads: 'all',
  filterUsage: 'charts',
};

function formatDateInput(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDefaultFilters(): MonitoringQuery {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 29);

  return {
    data_inicio: formatDateInput(startDate),
    data_fim: formatDateInput(endDate),
    granularidade: 'auto',
    limite: 10,
  };
}

function getErrorMessage(error: unknown) {
  const response = (error as { response?: { status?: number; data?: { detail?: string } } }).response;

  if (response?.status === 403) return 'Você não possui permissão para consultar os dados de monitoramento.';
  if (response?.status === 400) return response.data?.detail || 'Os filtros informados não são válidos.';
  return 'Não foi possível carregar os dados desta seção. Tente novamente.';
}

function getInitialSections() {
  const filters = getDefaultFilters();

  return sectionKeys.reduce((sections, key) => {
    sections[key] = { filters: { ...filters }, data: null, loading: true, error: '' };
    return sections;
  }, {} as Record<MonitoringSectionKey, MonitoringSectionState>);
}

export function useMonitoringSections() {
  const [sections, setSections] = useState(getInitialSections);
  const [globalFilters, setGlobalFilters] = useState<Pick<MonitoringQuery, 'data_inicio' | 'data_fim'>>(() => {
    const defaults = getDefaultFilters();
    return { data_inicio: defaults.data_inicio, data_fim: defaults.data_fim };
  });
  const controllers = useRef<Partial<Record<MonitoringSectionKey, AbortController>>>({});

  useEffect(() => {
    const controller = new AbortController();
    const initialFilters = getDefaultFilters();
    let active = true;

    getMonitoringMetrics(initialFilters, controller.signal)
      .then(response => {
        if (!active) return;

        setSections(current => {
          const updated = { ...current };
          sectionKeys.forEach(key => {
            updated[key] = { ...current[key], data: response.data, loading: false, error: '' };
          });
          return updated;
        });
      })
      .catch(error => {
        if (!active || (error as { code?: string }).code === 'ERR_CANCELED') return;

        setSections(current => {
          const updated = { ...current };
          sectionKeys.forEach(key => {
            updated[key] = { ...current[key], loading: false, error: getErrorMessage(error) };
          });
          return updated;
        });
      });

    return () => {
      active = false;
      controller.abort();
      Object.values(controllers.current).forEach(sectionController => sectionController?.abort());
    };
  }, []);

  const applyFilters = useCallback((key: MonitoringSectionKey, filters: MonitoringQuery) => {
    controllers.current[key]?.abort();

    if (key === 'sessionLocations') {
      setSections(current => ({
        ...current,
        [key]: { ...current[key], filters, loading: false, error: '' },
      }));
      return;
    }

    const controller = new AbortController();
    controllers.current[key] = controller;

    setSections(current => ({
      ...current,
      [key]: { ...current[key], filters, loading: true, error: '' },
    }));

    const requirement = requirements[key];
    const request = requirement === 'cards'
      ? getMonitoringCards(filters, controller.signal)
      : requirement === 'charts'
        ? getMonitoringCharts(filters, controller.signal)
        : getMonitoringMetrics(filters, controller.signal);

    request
      .then(response => {
        if (controllers.current[key] !== controller) return;

        setSections(current => ({
          ...current,
          [key]: {
            ...current[key],
            data: { ...current[key].data, ...response.data },
            loading: false,
            error: '',
          },
        }));
      })
      .catch(error => {
        if (controllers.current[key] !== controller || (error as { code?: string }).code === 'ERR_CANCELED') return;

        setSections(current => ({
          ...current,
          [key]: { ...current[key], loading: false, error: getErrorMessage(error) },
        }));
      });
  }, []);

  const clearFilters = useCallback(
    (key: MonitoringSectionKey) => applyFilters(key, getDefaultFilters()),
    [applyFilters],
  );

  const applyGlobalDateFilter = useCallback(
    (dateRange: Pick<MonitoringQuery, 'data_inicio' | 'data_fim'>) => {
      setGlobalFilters(dateRange);

      sectionKeys.forEach(key => {
        if (key === 'sessionLocations') {
          setSections(current => ({
            ...current,
            [key]: {
              ...current[key],
              filters: { ...current[key].filters, ...dateRange },
            },
          }));
          return;
        }

        controllers.current[key]?.abort();
        const controller = new AbortController();
        controllers.current[key] = controller;

        setSections(current => ({
          ...current,
          [key]: {
            ...current[key],
            filters: { ...current[key].filters, ...dateRange },
            loading: true,
            error: '',
          },
        }));

        const filters: MonitoringQuery = { ...getDefaultFilters(), ...dateRange };

        const requirement = requirements[key];
        const request = requirement === 'cards'
          ? getMonitoringCards(filters, controller.signal)
          : requirement === 'charts'
            ? getMonitoringCharts(filters, controller.signal)
            : getMonitoringMetrics(filters, controller.signal);

        request
          .then(response => {
            if (controllers.current[key] !== controller) return;
            setSections(current => ({
              ...current,
              [key]: {
                ...current[key],
                data: { ...current[key].data, ...response.data },
                loading: false,
                error: '',
              },
            }));
          })
          .catch(error => {
            if (controllers.current[key] !== controller || (error as { code?: string }).code === 'ERR_CANCELED') return;
            setSections(current => ({
              ...current,
              [key]: { ...current[key], loading: false, error: getErrorMessage(error) },
            }));
          });
      });
    },
    [],
  );

  const clearGlobalDateFilter = useCallback(() => {
    const defaults = getDefaultFilters();
    applyGlobalDateFilter({ data_inicio: defaults.data_inicio, data_fim: defaults.data_fim });
  }, [applyGlobalDateFilter]);

  return { sections, applyFilters, clearFilters, globalFilters, applyGlobalDateFilter, clearGlobalDateFilter };
}
